import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, addDoc, getDocs, doc, updateDoc,
  deleteDoc, getDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, TextField,
  IconButton, Alert, CircularProgress, Chip,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Accordion,
  AccordionSummary, AccordionDetails, Switch, Tooltip, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = [
  {
    id: 'grundlaggande',
    label: 'Grundläggande kompetenser',
    description: 'Alla i affärsområdet ska behärska dessa',
    color: '#2e7d32',
    bg: '#e8f5e9'
  },
  {
    id: 'nyckel',
    label: 'Nyckelkompetenser',
    description: 'Viktiga kompetenser för kärnuppdraget',
    color: '#1565c0',
    bg: '#e3f2fd'
  },
  {
    id: 'special',
    label: 'Specialkompetenser',
    description: 'Avancerade eller specialiserade kompetenser',
    color: '#6a1b9a',
    bg: '#f3e5f5'
  },
];

const PRESET_SKILLS = {
  BTM: {
    grundlaggande: [
      'Tikabanda', 'ABK09', 'AB/ABT', 'Byggprocessen',
      'Standard BH90', 'CAD-projektering', 'BIM-projektering',
      'Samordningsverktyg', 'Granskningsverktyg', 'Punktmoln',
      'Dokumentation'
    ],
    nyckel: [
      'Modellbaserade leveranser', 'Samordningsprocess',
      'Portalkonfiguration', 'Databas', 'Scripting',
      'Klassificering', 'IFC'
    ],
    special: [
      'Granskningsprocess', 'Strategi och metodik',
      'Hålla utbildningar', 'Leda möten',
      'Scanning utförande', 'Scanning kravställning',
      'Datasamordning', 'Mottagningskontroll modell',
      'Visualisering', 'SAK-miljoer', 'Produktion KMA',
      'VR', '4D', 'Projektledning', 'Kravhantering',
      'Tidplanering', 'PowerBI', 'WBS'
    ]
  },
  MEK: {
    grundlaggande: [
      'AutoCAD', 'SolidWorks', 'Inventor', 'Femap',
      'Ansys', 'Stålkonstruktion', 'Mekanisk konstruktion',
      'ABK09', 'Svets', 'Eurocode'
    ],
    nyckel: [
      'Ridas', 'ASME', 'Lyftdonsnorm', 'DIN19704',
      'SS-EN1090', 'SS-EN 15085', 'Utmattning', 'Gensys'
    ],
    special: [
      'HEC-RAS', 'Tryckkärl', 'Krav brobyggande',
      'Dynamik', 'Jordbävning',
      'Godkännande av järnvägsfordon'
    ]
  },
  IV: {
    grundlaggande: [
      'C#', 'C++', 'JavaScript', 'GIT',
      'Houdini', '3ds Max', 'C4D'
    ],
    nyckel: [
      'Unity', 'Unreal', 'ThreeJS', 'BabylonJS',
      'Python', 'AI', 'CAD'
    ],
    special: []
  }
};

export default function SkillsManager() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [skills, setSkills] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({
    open: false, deptId: '', category: '', editId: '', name: ''
  });
  const [importDialog, setImportDialog] = useState({
    open: false, deptId: '', deptName: ''
  });
  const [importing, setImporting] = useState(false);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setLoading(true);
    const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
    const userData = userSnap.exists() ? userSnap.data() : {};

    const deptSnap = await getDocs(collection(db, 'departments'));
    let depts = deptSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    if (userData.role !== 'superadmin') {
      depts = depts.filter(d => d.id === userData.departmentId);
    }
    setDepartments(depts);

    const skillsMap = {};
    for (const dept of depts) {
      const skillSnap = await getDocs(
        collection(db, 'departments', dept.id, 'skills')
      );
      skillsMap[dept.id] = skillSnap.docs.map(d => ({
        id: d.id, ...d.data()
      }));
    }
    setSkills(skillsMap);
    setLoading(false);
  }

  async function handleToggleSelfRating(deptId, current) {
    await updateDoc(doc(db, 'departments', deptId), {
      selfRatingEnabled: !current
    });
    setDepartments(prev => prev.map(d =>
      d.id === deptId ? { ...d, selfRatingEnabled: !current } : d
    ));
    setSaved(!current
      ? 'Självskattning aktiverad – medarbetare kan nu skatta sina kompetenser!'
      : 'Självskattning inaktiverad – fälten döljs för medarbetare.');
    setTimeout(() => setSaved(''), 4000);
  }

  async function handleSaveSkill() {
    const { deptId, category, editId, name } = dialog;
    if (!name.trim() || !deptId || !category) return;
    if (editId) {
      await updateDoc(
        doc(db, 'departments', deptId, 'skills', editId),
        { name: name.trim(), updatedAt: new Date() }
      );
    } else {
      await addDoc(
        collection(db, 'departments', deptId, 'skills'),
        {
          name: name.trim(),
          category,
          visibleToEmployee: false,
          createdAt: new Date(),
          createdBy: currentUser.uid
        }
      );
    }
    setSaved('Kompetens sparad!');
    setTimeout(() => setSaved(''), 2000);
    setDialog({ open: false, deptId: '', category: '', editId: '', name: '' });
    loadAll();
  }

  async function handleToggleVisible(deptId, skillId, current) {
    await updateDoc(
      doc(db, 'departments', deptId, 'skills', skillId),
      { visibleToEmployee: !current }
    );
    setSkills(prev => ({
      ...prev,
      [deptId]: prev[deptId].map(s =>
        s.id === skillId
          ? { ...s, visibleToEmployee: !current }
          : s
      )
    }));
  }

  async function handleDeleteSkill(deptId, skillId) {
    await deleteDoc(doc(db, 'departments', deptId, 'skills', skillId));
    setSkills(prev => ({
      ...prev,
      [deptId]: prev[deptId].filter(s => s.id !== skillId)
    }));
  }

  async function handleImportPresets(deptId, deptName) {
    setImporting(true);
    const aoKey = Object.keys(PRESET_SKILLS).find(key =>
      deptName.toUpperCase().includes(key)
    );
    if (!aoKey) {
      setError('Hittade inga förinlagda kompetenser för ' + deptName);
      setTimeout(() => setError(''), 3000);
      setImporting(false);
      return;
    }
    const presets = PRESET_SKILLS[aoKey];
    let count = 0;
    for (const [category, skillNames] of Object.entries(presets)) {
      for (const name of skillNames) {
        const existing = (skills[deptId] || []).find(
          s => s.name.toLowerCase() === name.toLowerCase()
        );
        if (!existing) {
          await addDoc(
            collection(db, 'departments', deptId, 'skills'),
            {
              name, category,
              visibleToEmployee: false,
              createdAt: new Date(),
              createdBy: currentUser.uid,
              fromPreset: true
            }
          );
          count++;
        }
      }
    }
    setSaved(count + ' kompetenser importerade! Aktivera de som medarbetarna ska skatta.');
    setTimeout(() => setSaved(''), 5000);
    setImportDialog({ open: false, deptId: '', deptName: '' });
    setImporting(false);
    loadAll();
  }

  function getSkillsByCategory(deptId, category) {
    return (skills[deptId] || [])
      .filter(s => s.category === category)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  function getVisibleCount(deptId) {
    return (skills[deptId] || []).filter(s => s.visibleToEmployee).length;
  }

  function getTotalCount(deptId) {
    return (skills[deptId] || []).length;
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <PsychologyIcon />
          <Typography variant="h5" fontWeight="bold">
            Kompetenshantering
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Förklaring */}
        <Paper sx={{ p: 2.5, mb: 3, bgcolor: '#f3f4ff',
          border: '1px solid #c5cae9' }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: '#3949ab', fontSize: 20 }} />
              <Typography variant="body2">
                <strong>Självskattning på/av</strong> – styr om
                medarbetare kan skatta sina kompetenser
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilityIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
              <Typography variant="body2">
                <strong>Synlig</strong> – visas för medarbetare
                att skatta (kräver även att självskattning är på)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilityOffIcon sx={{ color: '#999', fontSize: 20 }} />
              <Typography variant="body2">
                <strong>Intern</strong> – bara GL skattar i GAP-analysen
              </Typography>
            </Box>
          </Box>
        </Paper>

        {departments.map(dept => (
          <Paper key={dept.id} sx={{ mb: 3, overflow: 'hidden' }}>

            {/* AO-header */}
            <Box sx={{
              p: 2.5, bgcolor: '#1a1a1a', color: 'white',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {dept.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#aaa' }}>
                    {getTotalCount(dept.id)} kompetenser –{' '}
                    {getVisibleCount(dept.id)} synliga för medarbetare
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center',
                  gap: 1.5, flexWrap: 'wrap' }}>

                  {/* Självskattning toggle */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    bgcolor: dept.selfRatingEnabled
                      ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.05)',
                    border: '1px solid',
                    borderColor: dept.selfRatingEnabled
                      ? '#4caf50' : 'rgba(255,255,255,0.15)',
                    borderRadius: 2, px: 1.5, py: 0.5
                  }}>
                    <PeopleIcon sx={{
                      fontSize: 16,
                      color: dept.selfRatingEnabled ? '#4caf50' : '#888'
                    }} />
                    <Typography variant="caption"
                      sx={{ color: dept.selfRatingEnabled
                        ? '#4caf50' : '#888' }}>
                      Självskattning
                    </Typography>
                    <Switch
                      size="small"
                      checked={!!dept.selfRatingEnabled}
                      onChange={() => handleToggleSelfRating(
                        dept.id, !!dept.selfRatingEnabled)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#4caf50'
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#4caf50'
                        }
                      }}
                    />
                    <Chip size="small"
                      label={dept.selfRatingEnabled ? 'PÅ' : 'AV'}
                      sx={{
                        bgcolor: dept.selfRatingEnabled
                          ? '#1b5e20' : '#333',
                        color: 'white', fontSize: '0.6rem', height: 18
                      }}
                    />
                  </Box>

                  <Chip
                    icon={<VisibilityIcon style={{ color: '#4caf50' }} />}
                    label={getVisibleCount(dept.id) + ' synliga'}
                    size="small"
                    sx={{ bgcolor: '#1b5e20', color: 'white',
                      fontSize: '0.7rem' }}
                  />
                  <Chip
                    label={getTotalCount(dept.id) + ' totalt'}
                    size="small"
                    sx={{ bgcolor: '#FF6B00', color: 'white',
                      fontSize: '0.7rem' }}
                  />
                  {Object.keys(PRESET_SKILLS).some(key =>
                    dept.name.toUpperCase().includes(key)
                  ) && (
                    <Button size="small" variant="outlined"
                      sx={{ color: 'white',
                        borderColor: 'rgba(255,255,255,0.4)',
                        fontSize: '0.7rem' }}
                      onClick={() => setImportDialog({
                        open: true, deptId: dept.id, deptName: dept.name
                      })}>
                      Importera från GAP-mall
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Självskattning-status banner */}
            {dept.selfRatingEnabled && (
              <Box sx={{
                px: 2.5, py: 1, bgcolor: '#e8f5e9',
                borderBottom: '1px solid #c8e6c9',
                display: 'flex', alignItems: 'center', gap: 1
              }}>
                <PeopleIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
                <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                  <strong>Självskattning är aktiverad</strong> –
                  medarbetare ser och kan skatta kompetenser
                  markerade som synliga.
                </Typography>
              </Box>
            )}

            {/* Tom-state */}
            {getTotalCount(dept.id) === 0 && (
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa' }}>
                <PsychologyIcon sx={{ fontSize: 40, color: '#ddd', mb: 1 }} />
                <Typography variant="body2" color="text.secondary"
                  gutterBottom>
                  Inga kompetenser tillagda ännu.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Lägg till manuellt eller importera från GAP-analys-mallen.
                </Typography>
              </Box>
            )}

            {/* Kategorier */}
            <Box sx={{ p: 2 }}>
              {CATEGORIES.map(cat => {
                const catSkills = getSkillsByCategory(dept.id, cat.id);
                const visibleInCat = catSkills.filter(
                  s => s.visibleToEmployee).length;

                return (
                  <Accordion key={cat.id} defaultExpanded
                    sx={{ mb: 1, '&:before': { display: 'none' },
                      border: '1px solid', borderColor: 'divider' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                      sx={{ bgcolor: cat.bg }}>
                      <Box sx={{ display: 'flex', alignItems: 'center',
                        gap: 1.5, width: '100%' }}>
                        <Box sx={{ width: 10, height: 10,
                          borderRadius: '50%', bgcolor: cat.color,
                          flexShrink: 0 }} />
                        <Typography fontWeight="bold"
                          sx={{ color: cat.color, flex: 1 }}>
                          {cat.label}
                        </Typography>
                        <Chip size="small"
                          label={visibleInCat + '/' + catSkills.length +
                            ' synliga'}
                          sx={{ bgcolor: 'white', color: cat.color,
                            fontSize: '0.65rem', height: 20,
                            border: '1px solid', borderColor: cat.color }}
                        />
                        <Typography variant="caption"
                          color="text.secondary" sx={{ mr: 1 }}>
                          {cat.description}
                        </Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 1.5 }}>
                      <List dense disablePadding>
                        {catSkills.map(skill => (
                          <ListItem key={skill.id} sx={{
                            bgcolor: skill.visibleToEmployee
                              ? cat.bg : '#f5f5f5',
                            borderRadius: 1, mb: 0.5,
                            border: '1px solid',
                            borderColor: skill.visibleToEmployee
                              ? cat.color + '40' : 'transparent',
                            transition: 'all 0.2s'
                          }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex',
                                  alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{
                                    color: skill.visibleToEmployee
                                      ? cat.color : 'text.secondary',
                                    fontWeight: skill.visibleToEmployee
                                      ? 600 : 400
                                  }}>
                                    {skill.name}
                                  </Typography>
                                  {!skill.visibleToEmployee && (
                                    <Chip label="Intern" size="small"
                                      sx={{ height: 16, fontSize: '0.6rem',
                                        bgcolor: '#eee', color: '#999' }} />
                                  )}
                                  {skill.visibleToEmployee &&
                                    !dept.selfRatingEnabled && (
                                    <Chip label="Synlig men självsk. av"
                                      size="small"
                                      sx={{ height: 16, fontSize: '0.6rem',
                                        bgcolor: '#fff3e0',
                                        color: '#e65100' }} />
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: 'flex',
                                alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={
                                  skill.visibleToEmployee
                                    ? 'Synlig för medarbetare – klicka för att dölj'
                                    : 'Intern – klicka för att visa för medarbetare'
                                }>
                                  <Box sx={{ display: 'flex',
                                    alignItems: 'center', gap: 0.5 }}>
                                    {skill.visibleToEmployee
                                      ? <VisibilityIcon sx={{
                                          fontSize: 16, color: '#2e7d32'
                                        }} />
                                      : <VisibilityOffIcon sx={{
                                          fontSize: 16, color: '#bbb'
                                        }} />
                                    }
                                    <Switch size="small"
                                      checked={!!skill.visibleToEmployee}
                                      onChange={() => handleToggleVisible(
                                        dept.id, skill.id,
                                        !!skill.visibleToEmployee
                                      )}
                                      sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: cat.color
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: cat.color
                                        }
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                                <IconButton size="small"
                                  onClick={() => setDialog({
                                    open: true,
                                    deptId: dept.id,
                                    category: cat.id,
                                    editId: skill.id,
                                    name: skill.name
                                  })}>
                                  <EditIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                                <IconButton size="small" color="error"
                                  onClick={() => handleDeleteSkill(
                                    dept.id, skill.id)}>
                                  <DeleteIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}

                        {catSkills.length === 0 && (
                          <Box sx={{ py: 1.5, px: 1 }}>
                            <Typography variant="caption"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic' }}>
                              Inga kompetenser i denna kategori
                            </Typography>
                          </Box>
                        )}
                      </List>

                      <Divider sx={{ my: 1 }} />

                      <Button size="small" startIcon={<AddIcon />}
                        variant="outlined"
                        sx={{ borderColor: cat.color, color: cat.color,
                          '&:hover': { bgcolor: cat.bg } }}
                        onClick={() => setDialog({
                          open: true,
                          deptId: dept.id,
                          category: cat.id,
                          editId: '',
                          name: ''
                        })}>
                        Lägg till kompetens
                      </Button>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Paper>
        ))}

        {departments.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Inga affärsområden att hantera.
            </Typography>
          </Paper>
        )}
      </Container>

      {/* Dialog lägg till/redigera */}
      <Dialog open={dialog.open}
        onClose={() => setDialog({ ...dialog, open: false })}
        maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialog.editId ? 'Redigera kompetens' : 'Lägg till kompetens'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Kategori</InputLabel>
            <Select value={dialog.category} label="Kategori"
              onChange={e => setDialog(prev => ({
                ...prev, category: e.target.value
              }))}>
              {CATEGORIES.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField autoFocus fullWidth label="Kompetensnamn"
            value={dialog.name}
            onChange={e => setDialog(prev => ({
              ...prev, name: e.target.value
            }))}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSaveSkill();
            }}
          />
          <Alert severity="info" sx={{ mt: 2 }} icon={false}>
            <Typography variant="caption">
              Ny kompetens skapas som <strong>intern</strong> –
              aktivera synlighet med togglen om medarbetarna
              ska kunna skatta sig själva.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() =>
            setDialog({ ...dialog, open: false })}>
            Avbryt
          </Button>
          <Button variant="contained" onClick={handleSaveSkill}
            disabled={!dialog.name.trim()}>
            Spara
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog importera från GAP-mall */}
      <Dialog open={importDialog.open}
        onClose={() =>
          setImportDialog({ ...importDialog, open: false })}
        maxWidth="xs" fullWidth>
        <DialogTitle>Importera från GAP-analys-mall</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Vill du importera förinlagda kompetenser från
            Tikabs GAP-analys-mall för{' '}
            <strong>{importDialog.deptName}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            <Typography variant="caption">
              Alla importerade kompetenser skapas som{' '}
              <strong>interna</strong>. Du aktiverar själv vilka
              medarbetarna ska skatta. Kompetenser som redan
              finns läggs inte till igen.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() =>
            setImportDialog({ ...importDialog, open: false })}>
            Avbryt
          </Button>
          <Button variant="contained"
            disabled={importing}
            startIcon={importing
              ? <CircularProgress size={16} color="inherit" />
              : null}
            onClick={() => handleImportPresets(
              importDialog.deptId, importDialog.deptName)}>
            {importing ? 'Importerar...' : 'Importera'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}